import {
  isDuplicateFilterAllowedColumnType,
  isValidArrayInnerType,
  parseSystemColumnFieldId,
} from '@doss/common-all'
import {
  GridFilterOperator,
  getGridBooleanOperators as muiGetGridBooleanOperators,
  getGridDateOperators as muiGetGridDateOperators,
  getGridNumericOperators as muiGetGridNumericOperators,
  getGridSingleSelectOperators as muiGetGridSingleSelectOperators,
  getGridStringOperators as muiGetGridStringOperators,
} from '@mui/x-data-grid-premium'
import { orderBy, startCase } from 'lodash'
import { JSXElementConstructor } from 'react'
import { Option } from '~/@/components/inputs/selection/utils'
import { getUserFilterOptions } from '~/common/utils/table'
import { buildEnumDropdownOptions } from '~/components/shared/enum-options'
import { CustomFilterInput } from '~/components/shared/Table/CustomFilterInput'
import {
  CustomMultiSelectFilterInput,
  CustomMultiSelectFilterInputProps,
} from '~/components/shared/Table/CustomMultiSelectFilterInput'
import { CustomNumericFilterInput } from '~/components/shared/Table/CustomNumericFilterInput'
import { CustomSingleSelectFilterInput } from '~/components/shared/Table/CustomSingleSelectFilterInput'
import { DynamicRangeFilterInput } from '~/components/shared/Table/DynamicRangeFilterInput'
import { RangeFilterInput } from '~/components/shared/Table/RangeFilterInput'
import { SingleDateFilterInput } from '~/components/shared/Table/SingleDateFilterInput'
import { TableColumnOption } from '~/components/shared/Table/TableColumnAutocomplete'
import { ActiveUser } from '../../components/settings/types'
import { DataType, IndexType } from '../generated/apollo-operations'

// No `label` on purpose: FilterOperatorSelector renders this operator from
// `table.filter_operator_is_duplicate`, so hardcoding the copy here would only ever drift from it.
const DUPLICATE_FILTER_OPERATOR: GridFilterOperator = {
  value: 'isDuplicate',
  getApplyFilterFn: () => null,
  requiresFilterValue: false,
}

const addDuplicateFilterOperator = (
  operators: GridFilterOperator[],
  columnType: DataType,
  includeDuplicateOperator: boolean,
): GridFilterOperator[] => {
  if (!includeDuplicateOperator || !isDuplicateFilterAllowedColumnType(columnType)) {
    return operators
  }
  return [...operators, DUPLICATE_FILTER_OPERATOR]
}

type GetGridFilterOperatorsOptions = {
  activeUsers?: ActiveUser[]
  disableFields?: boolean
  includeDuplicateOperator?: boolean
}

/**
 * Gets the single select operators for MUI and updates them to use the custom input component
 */
export const getGridSingleSelectOperators = (
  options: string[] | Option[],
  disabled: boolean,
): GridFilterOperator[] => {
  const muiSingleSelectOperators = muiGetGridSingleSelectOperators()
  const originalOperations = muiSingleSelectOperators.map((operator) => {
    let InputComponent = undefined
    let InputComponentProps = undefined
    if (operator.value === 'isAnyOf' || operator.value === 'isNoneOf') {
      InputComponent = CustomMultiSelectFilterInput
      InputComponentProps = { type: 'text', options, disabled }
    } else {
      InputComponent = CustomSingleSelectFilterInput
      InputComponentProps = { type: 'text', options, disabled }
    }

    return {
      ...operator,
      label: startCase(operator.value),
      InputComponent,
      InputComponentProps,
    }
  })

  // Add custom isNoneOf operator since MUI doesn't provide it by default
  const customOperators: GridFilterOperator[] = [
    {
      value: 'isNoneOf',
      label: 'Is None Of',
      getApplyFilterFn: () => null,
      InputComponent: CustomMultiSelectFilterInput,
      InputComponentProps: { type: 'text', options, disabled },
    },
  ]

  const emptyCheckOperators: GridFilterOperator[] = [
    {
      value: 'isEmpty',
      label: 'is empty',
      getApplyFilterFn: () => null,
      requiresFilterValue: false,
    },
    {
      value: 'isNotEmpty',
      label: 'is not empty',
      getApplyFilterFn: () => null,
      requiresFilterValue: false,
    },
  ]

  return [...originalOperations, ...customOperators, ...emptyCheckOperators]
}

/**
 * Gets the boolean operators for MUI and updates them to use the custom input component
 */
export const getGridBooleanOperators = (disabled: boolean): GridFilterOperator[] => {
  const muiBooleanOperators = muiGetGridBooleanOperators()
  return muiBooleanOperators.map((operator) => ({
    ...operator,
    label: startCase(operator.value),
    InputComponent: CustomSingleSelectFilterInput,
    InputComponentProps: { options: ['true', 'false'], disabled },
  }))
}

export const getGridDateOperators = (
  isDateTime = false,
  disabled: boolean,
): GridFilterOperator[] => {
  // Get original MUI date operators but override their input component
  const muiDateOperators = muiGetGridDateOperators()
  const originalOperators = muiDateOperators.map((operator) => ({
    ...operator,
    label: startCase(operator.value),
    ...(operator.value !== 'isEmpty' &&
      operator.value !== 'isNotEmpty' && {
        InputComponent: SingleDateFilterInput,
        InputComponentProps: { withTime: isDateTime, disabled },
      }),
  }))

  // Handle filters that are only for date time type columns
  const dateTimeFilterOperators: GridFilterOperator[] = isDateTime
    ? [
        {
          value: 'isOn',
          label: 'is on',
          getApplyFilterFn: () => null,
          InputComponent: SingleDateFilterInput,
          InputComponentProps: { withTime: false },
        },
        {
          value: 'notOn',
          label: 'not on',
          getApplyFilterFn: () => null,
          InputComponent: SingleDateFilterInput,
          InputComponentProps: { withTime: false },
        },
      ]
    : []

  // Add dynamic range operators
  const dynamicRangeOperators: GridFilterOperator[] = [
    {
      label: 'last',
      value: 'last',
      InputComponent: DynamicRangeFilterInput,
      InputComponentProps: {
        operator: 'last',
        isDateTime,
      },
      getApplyFilterFn: () => null,
    },
    {
      label: 'in the next',
      value: 'inTheNext',
      InputComponent: DynamicRangeFilterInput,
      InputComponentProps: {
        operator: 'inTheNext',
        isDateTime,
      },
      getApplyFilterFn: () => null,
    },
    {
      label: 'this',
      value: 'this',
      InputComponent: DynamicRangeFilterInput,
      InputComponentProps: {
        operator: 'this',
        isDateTime,
      },
      getApplyFilterFn: () => null,
    },
  ]

  // Add between operators using regular range picker
  const betweenOperators: GridFilterOperator[] = [
    {
      label: 'between',
      value: 'between',
      InputComponent: RangeFilterInput,
      InputComponentProps: {
        isDateTime,
      },
      getApplyFilterFn: () => null,
      getValueAsString: (value) => {
        if (!value || !value.from || !value.to) {
          return ''
        }
        const format = isDateTime ? 'MM/DD/YYYY h:mm A' : 'MM/DD/YYYY'
        return `${value.from.format(format)} - ${value.to.format(format)}`
      },
    },
    {
      label: 'not between',
      value: 'notBetween',
      InputComponent: RangeFilterInput,
      InputComponentProps: {
        isDateTime,
      },
      getApplyFilterFn: () => null,
      getValueAsString: (value) => {
        if (!value || !value.from || !value.to) {
          return ''
        }
        const format = isDateTime ? 'MM/DD/YYYY h:mm A' : 'MM/DD/YYYY'
        return `${value.from.format(format)} - ${value.to.format(format)}`
      },
    },
  ]

  // Combine all operators
  return [
    ...dynamicRangeOperators,
    ...betweenOperators,
    ...originalOperators,
    ...dateTimeFilterOperators,
  ]
}

export const getGridNumericOperators = (disabled: boolean): GridFilterOperator[] => {
  const muiNumericOperators = muiGetGridNumericOperators()
  const originalOperators = muiNumericOperators.map((operator) => {
    let InputComponent = undefined
    let InputComponentProps = undefined

    if (operator.value === 'isAnyOf' || operator.value === 'isNoneOf') {
      InputComponent = CustomMultiSelectFilterInput
      InputComponentProps = { type: 'number', options: [], freeSolo: true, disabled }
    } else if (operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty') {
      InputComponent = CustomNumericFilterInput
      InputComponentProps = { type: 'number', disabled }
    }

    return {
      ...operator,
      label: startCase(operator.value) || operator.value, // Fallback to original value if transformations result in empty string
      ...(InputComponent && {
        InputComponent,
        InputComponentProps,
      }),
    }
  })

  // Add custom isNoneOf operator since MUI doesn't provide it by default
  const customOperators: GridFilterOperator[] = [
    {
      value: 'isNoneOf',
      label: 'Is None Of',
      getApplyFilterFn: () => null,
      InputComponent: CustomMultiSelectFilterInput,
      InputComponentProps: { type: 'number', options: [], freeSolo: true, disabled },
    },
  ]

  return [...originalOperators, ...customOperators]
}

export const getGridStringOperators = (disabled: boolean): GridFilterOperator[] => {
  const muiStringOperators = muiGetGridStringOperators()
  const customOperators = muiStringOperators.map((operator) => {
    let InputComponent = undefined
    let InputComponentProps = undefined

    if (operator.value === 'isAnyOf' || operator.value === 'isNoneOf') {
      InputComponent = CustomMultiSelectFilterInput
      InputComponentProps = { type: 'text', options: [], freeSolo: true, disabled }
    } else if (operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty') {
      InputComponent = CustomFilterInput
      InputComponentProps = { type: 'text', disabled }
    }

    return {
      ...operator,
      label: startCase(operator.value),
      ...(InputComponent && {
        InputComponent,
        InputComponentProps,
      }),
    }
  })

  // Add custom isNoneOf operator since MUI doesn't provide it by default
  const isNoneOfOperator: GridFilterOperator[] = [
    {
      value: 'isNoneOf',
      label: 'Is None Of',
      getApplyFilterFn: () => null,
      InputComponent: CustomMultiSelectFilterInput,
      InputComponentProps: { type: 'text', options: [], freeSolo: true, disabled },
    },
  ]

  // Add custom IN and NOT_IN operators
  const inOperators: GridFilterOperator[] = [
    {
      value: 'in',
      label: 'in',
      getApplyFilterFn: () => null,
      InputComponent: CustomFilterInput,
      InputComponentProps: { type: 'text', disabled },
    },
    {
      value: 'notIn',
      label: 'not in',
      getApplyFilterFn: () => null,
      InputComponent: CustomFilterInput,
      InputComponentProps: { type: 'text', disabled },
    },
  ]

  const doesNotContainOperator: GridFilterOperator = {
    value: 'doesNotContain',
    label: 'Does Not Contain',
    getApplyFilterFn: () => null,
    InputComponent: CustomFilterInput,
    InputComponentProps: { type: 'text', disabled },
  }
  const allOperators: GridFilterOperator[] = [
    ...customOperators,
    ...isNoneOfOperator,
    ...inOperators,
    doesNotContainOperator,
  ]

  // desired order of operators
  const operatorOrder = [
    'equals',
    'contains',
    'doesNotContain',
    'isEmpty',
    'isNotEmpty',
    'isAnyOf',
    'isNoneOf',
    'in',
    'notIn',
    'startsWith',
    'endsWith',
  ]

  // sort operators based on the custom order
  return orderBy(allOperators, [
    (op) => {
      const index = operatorOrder.indexOf(op.value)
      return index === -1 ? operatorOrder.length : index
    },
  ])
}

// `resolvedSourceColumn.column` is the final column in a lookup chain, and the column itself when
// there is no lookup, so its set covers direct and looked-up enums alike
function getEnumFilterOptions(column: Pick<TableColumnOption, 'resolvedSourceColumn'>): Option[] {
  return buildEnumDropdownOptions(column.resolvedSourceColumn?.column.enumSet?.options)
}

/**
 * Returns the MUI Grid Filter operators for the given column
 */
const getBaseGridFilterOperators = (
  column: TableColumnOption,
  activeUsers: ActiveUser[] | undefined,
  disableFields: boolean,
): GridFilterOperator[] => {
  // For lookup columns, use resolvedSourceType to handle multi-level lookups
  const columnType = column.type === DataType.LOOKUP ? column.resolvedSourceType : column.type

  switch (columnType) {
    case DataType.ENUM:
      return getGridSingleSelectOperators(getEnumFilterOptions(column), disableFields)
    case DataType.USER:
      return getGridSingleSelectOperators(getUserFilterOptions(activeUsers), disableFields)
    case DataType.NUMBER:
    case DataType.FORMULA:
    case DataType.FORMULA_NUMBER:
      return getGridNumericOperators(disableFields)

    // FORMULA_BOOLEAN is intentionally out of scope for the non-numeric-formulas epic (see
    // isFormula()'s doc comment in @doss/common-all's table-cell.ts) -- no cell-data schema or
    // resolver exists for it yet.
    case DataType.FORMULA_BOOLEAN:
      return []
    case DataType.BOOLEAN:
      return getGridBooleanOperators(disableFields)
    case DataType.FORMULA_DATE:
    case DataType.DATE:
      return getGridDateOperators(false, disableFields)
    case DataType.FORMULA_DATE_TIME:
    case DataType.DATE_TIME:
      return getGridDateOperators(true, disableFields)
    case DataType.FORMULA_STRING:
    case DataType.STRING:
    case DataType.FILE_URL:
      return getGridStringOperators(disableFields)
    case DataType.ID: {
      switch (column.indexType) {
        case IndexType.NONE:
          throw new Error(`ID column ${column.id} has no index type`)
        case IndexType.NUMBER:
          return getGridNumericOperators(disableFields)
        case IndexType.STRING:
          return getGridStringOperators(disableFields)
      }
      break
    }
    case DataType.LOOKUP:
      // This should not happen as resolvedSourceType should resolve through lookups
      // Fallback to string operators for safety
      return getGridStringOperators(disableFields)
    case DataType.ARRAY: {
      return getArrayFilterOperators(column, activeUsers, disableFields)
    }
  }
}

/**
 * Returns the MUI Grid Filter operators for the given column.
 */
export const getGridFilterOperators = (
  column: TableColumnOption,
  {
    activeUsers,
    disableFields = false,
    includeDuplicateOperator = false,
  }: GetGridFilterOperatorsOptions = {},
): GridFilterOperator[] => {
  const operators = getBaseGridFilterOperators(column, activeUsers, disableFields)

  // Deliberately check the unresolved type: lookups and formulas are ineligible even when their
  // resolved source type has otherwise supported base operators.
  return addDuplicateFilterOperator(
    operators,
    column.type,
    includeDuplicateOperator && parseSystemColumnFieldId(column.id) === null,
  )
}

/**
 * Returns the custom array filter operators for array type columns. Array columns only support
 * "contains", "does not contain", "is empty", and "is not empty" operators which can only support
 * input components that allow multiple values.
 */
export const getArrayFilterOperators = (
  tableColumnHeader: Pick<
    TableColumnOption,
    'id' | 'name' | 'innerType' | 'lookupColumn' | 'resolvedSourceType' | 'resolvedSourceColumn'
  >,
  activeUsers: ActiveUser[] | undefined,
  disableFields: boolean,
): GridFilterOperator[] => {
  if (!isValidArrayInnerType(tableColumnHeader.innerType)) {
    throw new Error(`Invalid inner type for array column ${tableColumnHeader.id}`)
  }

  let InputComponent: JSXElementConstructor<CustomMultiSelectFilterInputProps>
  const InputComponentProps: Partial<CustomMultiSelectFilterInputProps> = {
    options: [],
  }

  // Use resolvedSourceType
  const effectiveType = tableColumnHeader.resolvedSourceType

  switch (effectiveType) {
    case DataType.ENUM:
      InputComponent = CustomMultiSelectFilterInput
      InputComponentProps.options = getEnumFilterOptions(tableColumnHeader)
      break
    case DataType.FILE_URL:
      InputComponent = CustomMultiSelectFilterInput
      break
    case DataType.USER: {
      InputComponent = CustomMultiSelectFilterInput
      InputComponentProps.options = getUserFilterOptions(activeUsers)
      break
    }
    case DataType.NUMBER:
      InputComponent = CustomMultiSelectFilterInput
      InputComponentProps.type = 'number'
      break
    case DataType.STRING:
      InputComponent = CustomMultiSelectFilterInput
      break
    case DataType.ID: {
      if (!tableColumnHeader.lookupColumn) {
        InputComponent = CustomMultiSelectFilterInput
        break
      }
      switch (tableColumnHeader.lookupColumn.indexType) {
        case IndexType.NONE:
          throw new Error(`ID column ${tableColumnHeader.lookupColumn.id} has no index type`)
        case IndexType.NUMBER:
          InputComponent = CustomMultiSelectFilterInput
          InputComponentProps.type = 'number'
          break
        case IndexType.STRING:
          InputComponent = CustomMultiSelectFilterInput
          break
      }
      break
    }
    case DataType.LOOKUP: {
      // This code should not be reached as resolvedSourceType should resolve through lookups
      // but adding as a fallback for safety
      if (!tableColumnHeader.lookupColumn) {
        InputComponent = CustomMultiSelectFilterInput
        break
      }
      const lookup = tableColumnHeader.lookupColumn
      InputComponent = CustomMultiSelectFilterInput
      if (lookup.type === DataType.ENUM) {
        InputComponentProps.options = getEnumFilterOptions(tableColumnHeader)
      } else if (lookup.type === DataType.USER) {
        InputComponentProps.options = getUserFilterOptions(activeUsers)
      } else if (lookup.type === DataType.NUMBER) {
        InputComponentProps.type = 'number'
      }
      break
    }
    case DataType.BOOLEAN:
    case DataType.DATE:
    case DataType.DATE_TIME:
    case DataType.ARRAY:
    case DataType.FORMULA:
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      InputComponent = CustomMultiSelectFilterInput
      break
    // FORMULA_BOOLEAN is intentionally out of scope for the non-numeric-formulas epic (see
    // isFormula()'s doc comment in @doss/common-all's table-cell.ts) -- no cell-data schema or
    // resolver exists for it yet.
    case DataType.FORMULA_BOOLEAN:
      return []
  }

  InputComponentProps.disabled = disableFields

  return [
    {
      value: 'contains',
      getApplyFilterFn: () => null,
      InputComponent,
      InputComponentProps,
    },
    {
      value: 'doesNotContain',
      label: 'does not contain',
      getApplyFilterFn: () => null,
      InputComponent,
      InputComponentProps,
    },
    {
      value: 'isEmpty',
      label: 'is empty',
      getApplyFilterFn: () => null,
      requiresFilterValue: false,
    },
    {
      value: 'isNotEmpty',
      label: 'is not empty',
      getApplyFilterFn: () => null,
      requiresFilterValue: false,
    },
  ]
}
